import { GroupAPIResponse } from '../../service/group/group.model';
import { normalize } from 'normalizr';
import LocationAPI from '../../service/locations/LocationAPI';
import Environment from '../../shared/constants/Environment';
import { FlexeserveGroupStructure, groupsListSchema } from './GroupsAdapter';
import {
  getTypeFromGroup,
  HierarchyLevel,
  parseLevelsFromHierarchy,
} from './util/getTypeFromGroup';
import { fromBase64, toBase64 } from '../../shared/util/base64';
import _ from 'lodash';
import { getCurrentWorkspaceSelector } from '../workspace/selectors/Workspace.selectors';

const StructureAPI = LocationAPI.injectEndpoints({
  endpoints: (build) => ({
    getStructureV2: build.query<
      FlexeserveGroupStructure & {
        businessUnits: string[];
        regions: string[];
        districts: string[];
        stores: string[];
        commanderIds: string[];
        levelGroups: Record<string, string[]>;
        hierarchy: HierarchyLevel[];
      },
      string | undefined
    >({
      queryFn: async (workspaceId, { getState }, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: workspaceId
            ? `${Environment.backendAPIWorkspace}/${toBase64(
                workspaceId
              )}/groups/tree`
            : Environment.backendAPIGroups,
          method: 'get',
        });

        if (result.error) {
          return { error: result.error as any };
        }

        const response = result.data as GroupAPIResponse[];

        // Read workspace settings from the store
        const state = getState();
        const workspace = getCurrentWorkspaceSelector(workspaceId)(
          state as any
        );

        const levels = parseLevelsFromHierarchy(
          workspace?.settings?.['flexeserveUI.hierarchy']
        );

        let hierarchy: HierarchyLevel[] = [];
        try {
          const raw = workspace?.settings?.['flexeserveUI.hierarchy'];
          if (raw) hierarchy = JSON.parse(raw);
        } catch {
          // ignore
        }

        const normalizedGroups: FlexeserveGroupStructure = normalize(
          response,
          groupsListSchema
        );

        const businessUnits: string[] = [];
        const regions: string[] = [];
        const districts: string[] = [];
        const stores: string[] = [];
        const commanderIds: string[] = [];
        const levelGroups: Record<string, string[]> = {};

        normalizedGroups.entities.groups =
          normalizedGroups.entities.groups || {};

        Object.values(normalizedGroups.entities.groups ?? {}).forEach(
          (group) => {
            const type = getTypeFromGroup(
              group,
              workspaceId,
              undefined,
              levels
            );
            group.businessLayerType = type;

            // has attribute with key FS_BusinessUnit_ID
            if (type === 'businessUnit') businessUnits.push(group.id);
            // has attribute with key FS_Region_ID
            if (type === 'region') regions.push(group.id);
            // has attribute with key FS_Market_ID
            if (type === 'district') districts.push(group.id);
            // has attribute with key FS_Store_ID
            if (type === 'store') stores.push(group.id);

            // Dynamic level grouping
            const fsLevel = group.attributes?.['FS_LEVEL'];
            if (fsLevel !== undefined) {
              if (!levelGroups[fsLevel]) levelGroups[fsLevel] = [];
              levelGroups[fsLevel].push(group.id);
            }

            if (group.devices?.length)
              // add all devices to commanderIds
              commanderIds.push(...group.devices.map(fromBase64));
          }
        );
        const extendedResponse = {
          ...normalizedGroups,
          businessUnits,
          regions,
          districts,
          stores,
          commanderIds: _.uniq(commanderIds),
          levelGroups,
          hierarchy,
        };
        return { data: extendedResponse };
      },
      providesTags: ['locations-actions', 'group-actions'],
    }),
  }),
});

export const { useGetStructureV2Query, useLazyGetStructureV2Query } =
  StructureAPI;
export default StructureAPI;
